import React, { useEffect, useMemo } from "react";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { Button } from "primereact/button";

import { IJobZone } from "../../JobZone.model";

import classes from "./SetJobZone.module.scss";
import { Dialog } from "primereact/dialog";
import { classNames as cx } from "primereact/utils";
import { InputText } from "primereact/inputtext";
import { useQuery } from "@tanstack/react-query";
import { requests } from "../../../../../services/http-common";
import { MultiSelect } from "primereact/multiselect";
import { EmployeeRoles } from "../../../../../models/Employee";
import { LabelValue } from "../../../../../components/LabelValue/LabelValue";

type SetJobZoneProps = {
  item: IJobZone | null;
  onHide: () => void;
  onAdd: (jobZone: IJobZone) => void;
  onUpdate: (jobZone: IJobZone) => void;
  requestPending: boolean;
};

const zoneManagerRoles = [
  EmployeeRoles.SUPERVISOR,
  EmployeeRoles.MANAGER,
  EmployeeRoles.ASSISTANT_MANAGER,
  EmployeeRoles.DOO,
];

export const SetJobZone = ({
  item,
  onAdd,
  onUpdate,
  onHide,
  requestPending,
}: SetJobZoneProps) => {
  const formSchema = Yup.object().shape({
    name: Yup.string().required("Field is required"),
    supervisorId: Yup.array().of(Yup.string()).min(1, "Field is required"),
    employeeId: Yup.array().of(Yup.string()),
  });

  const form = useForm<IJobZone>({
    resolver: yupResolver(formSchema),
    shouldFocusError: false,
    defaultValues: {
      name: "",
      employeeId: [],
      supervisorId: [],
    },
  });

  const { data: employeesPayload } = useQuery({
    queryKey: ["employee-list"],
    queryFn: () => {
      return requests.get(`/user?rows=9999999`);
    },
  });

  const employeeOptions = useMemo(() => {
    if (!employeesPayload) return [];
    const employeeOptions = employeesPayload.result.map((option: any) => ({
      label: `${option.firstName} ${option.lastName}`,
      value: option.id,
      role: option.role,
    }));
    return employeeOptions
      .filter((option: any) => option.role === EmployeeRoles.CLEANING_TECHNICIAN)
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [employeesPayload]);

  const supervisorOptions = useMemo(() => {
    if (!employeesPayload) return [];
    const employeeOptions = employeesPayload.result.map((option: any) => ({
      label: `${option.firstName} ${option.lastName}`,
      value: option.id,
      role: option.role,
    }));
    return employeeOptions
      .filter((option: any) => zoneManagerRoles.includes(option.role))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [employeesPayload]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = form;

  useEffect(() => {
    if (item?.zoneCode) {
      reset(item);
    }
    return () => reset();
  }, [item, reset]);

  const handleSave = (formData: IJobZone) => {
    if (item?.zoneCode) {
      onUpdate(formData);
      return;
    }
    onAdd(formData);
  };

  const productDialogFooter = (
    <React.Fragment>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text p-button-secondary"
        onClick={onHide}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-text p-button-primary"
        onClick={handleSubmit(handleSave)}
        disabled={requestPending}
      />
    </React.Fragment>
  );

  return (
    <Dialog
      visible={true}
      style={{ width: "450px" }}
      header="Details"
      modal
      className="p-fluid"
      footer={productDialogFooter}
      onHide={onHide}
      draggable={false}
    >
      <div className={classes.field}>
        <LabelValue label="Division Name" isRequired />
        <Controller
          name="name"
          render={({ field }) => (
            <InputText
              {...field}
              placeholder="Division Name"
              className={cx({
                "p-invalid": errors.name,
              })}
            />
          )}
          control={control}
        />
        {errors.name && (
          <small className="p-error">{errors.name?.message}</small>
        )}
      </div>
      <div className={classes.field}>
        <LabelValue label="Supervisors" isRequired />
        <Controller
          name="supervisorId"
          render={({ field }) => (
            <MultiSelect
              {...field}
              appendTo="self"
              options={supervisorOptions}
              placeholder="Select supervisors"
              style={{ margin: "5px 0" }}
              display="chip"
              maxSelectedLabels={2}
              className={cx({
                "p-invalid": errors.supervisorId,
              })}
            />
          )}
          control={control}
        />
        {errors.supervisorId && (
          <small className="p-error">{errors.supervisorId?.message}</small>
        )}
      </div>
      <div className={classes.field}>
        <LabelValue label="Employees" />
        <Controller
          name="employeeId"
          render={({ field }) => (
            <MultiSelect
              {...field}
              appendTo="self"
              options={employeeOptions}
              placeholder="Select employee"
              style={{ margin: "5px 0" }}
              display="chip"
              maxSelectedLabels={2}
            />
          )}
          control={control}
        />
      </div>
    </Dialog>
  );
};
